// react
import { useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';

// material ui
import {
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-premium';

// api
import {
  ActuatorSetPointDto,
  GetActuatorPlanDto,
  usePostActuatorPlanMutation,
} from '@/api/generated/recalc/endpoints/v2/gateways';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

interface ScheduleDatagridProps {
  plans?: GetActuatorPlanDto[] | null;
  isLoading?: boolean;
  gatewayNamesById?: Record<number, string>;
}

interface IntervalSegment {
  start: Date;
  end: Date;
  startSetpointIndex?: number;
  endSetpointIndex?: number;
}

interface DayIntervalView {
  startLabel: string;
  endLabel: string;
  interval: IntervalSegment;
}

interface DayCellValue {
  intervals: DayIntervalView[];
  colSpan: number;
  isContinuation?: boolean;
}

interface ChannelRow {
  id: string;
  gatewayName: string;
  channelLabel: string;
  isActivePlan: boolean;
  plan: GetActuatorPlanDto;
  d0: DayCellValue;
  d1: DayCellValue;
  d2: DayCellValue;
  d3: DayCellValue;
  d4: DayCellValue;
  d5: DayCellValue;
  d6: DayCellValue;
}

const WEEKDAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

const DANISH_LOCALE = 'da-DK';
const DANISH_TIMEZONE = 'Europe/Copenhagen';

const addDays = (date: Date, amount: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + amount);
  return result;
};

const startOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

const startOfWeekMonday = (date: Date): Date => {
  const result = startOfDay(date);
  const mondayBasedDay = (result.getDay() + 6) % 7;
  result.setDate(result.getDate() - mondayBasedDay);
  return result;
};

const toDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseTimestamp = (value?: string): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const toDateTimeLocalInput = (date: Date): string => {
  const withOffsetRemoved = new Date(
    date.getTime() - date.getTimezoneOffset() * 60 * 1000,
  );
  return withOffsetRemoved.toISOString().slice(0, 16);
};

const parseDateTimeLocalInput = (value: string): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const sortScheduleSetpoints = (
  schedule: ActuatorSetPointDto[] | null | undefined,
): ActuatorSetPointDto[] => {
  return [...(schedule ?? [])]
    .filter((setpoint) => Boolean(parseTimestamp(setpoint.timestamp)))
    .sort((a, b) => {
      const aTime = parseTimestamp(a.timestamp)?.getTime() ?? 0;
      const bTime = parseTimestamp(b.timestamp)?.getTime() ?? 0;
      return aTime - bTime;
    });
};

const buildClearScheduleFallback = (): ActuatorSetPointDto[] => [
  {
    timestamp: new Date().toISOString(),
    value: '100',
  },
];

// Build active display intervals from setpoint state changes.
const buildIntervals = (
  schedule: ActuatorSetPointDto[] | null | undefined,
): IntervalSegment[] => {
  if (!schedule || schedule.length === 0) return [];

  const sorted = [...schedule]
    .map((setpoint) => ({
      time: parseTimestamp(setpoint.timestamp),
      value: setpoint.value,
    }))
    .filter((setpoint) => Boolean(setpoint.time))
    .sort(
      (a, b) =>
        (a.time?.getTime() ?? Number.NEGATIVE_INFINITY) -
        (b.time?.getTime() ?? Number.NEGATIVE_INFINITY),
    );

  const intervals: IntervalSegment[] = [];

  const maxDate = new Date(8640000000000000);

  for (let index = 0; index < sorted.length; index += 1) {
    const current = sorted[index];
    const next = sorted[index + 1];

    if (!current.time) continue;
    if (current.value !== '0') continue;

    const end = next?.time ?? maxDate;
    if (!end) continue;

    const duration = end.getTime() - current.time.getTime();
    if (duration <= 0) continue;

    intervals.push({
      start: current.time,
      end,
      startSetpointIndex: index,
      endSetpointIndex: next ? index + 1 : undefined,
    });
  }

  return intervals;
};

const getPlansForDisplay = (
  plans: GetActuatorPlanDto[] | null | undefined,
): GetActuatorPlanDto[] => {
  return [...(plans ?? [])].sort((a, b) => {
    const aGateway = a.gatewayId ?? Number.MAX_SAFE_INTEGER;
    const bGateway = b.gatewayId ?? Number.MAX_SAFE_INTEGER;
    if (aGateway !== bGateway) return aGateway - bGateway;

    const channelSort = (a.channel ?? '').localeCompare(b.channel ?? '');
    if (channelSort !== 0) return channelSort;

    const aCreated = new Date(a.created ?? 0).getTime();
    const bCreated = new Date(b.created ?? 0).getTime();
    return bCreated - aCreated;
  });
};

const buildWeekDayIntervals = (
  intervals: IntervalSegment[],
  weekStart: Date,
): Record<string, DayCellValue> => {
  const map: Record<string, DayCellValue> = {};
  const weekEnd = addDays(weekStart, 7);
  const timeFormatter = new Intl.DateTimeFormat(DANISH_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: DANISH_TIMEZONE,
  });

  for (const interval of intervals) {
    if (interval.end <= weekStart || interval.start >= weekEnd) {
      continue;
    }

    const visibleStart =
      interval.start > weekStart ? interval.start : weekStart;
    const visibleEnd = interval.end < weekEnd ? interval.end : weekEnd;
    let cursor = startOfDay(visibleStart);

    while (cursor < visibleEnd) {
      const dayEnd = addDays(cursor, 1);
      const segmentStart = visibleStart > cursor ? visibleStart : cursor;
      const segmentEnd = visibleEnd < dayEnd ? visibleEnd : dayEnd;

      if (segmentEnd > segmentStart) {
        const key = toDateKey(cursor);
        if (!map[key]) {
          map[key] = {
            intervals: [],
            colSpan: 1,
          };
        }

        if (cursor.getTime() === startOfDay(visibleStart).getTime()) {
          const daysSpanned = Math.max(
            1,
            Math.ceil(
              (startOfDay(visibleEnd).getTime() -
                startOfDay(visibleStart).getTime()) /
                (24 * 60 * 60 * 1000),
            ) +
              (visibleEnd.getHours() > 0 ||
              visibleEnd.getMinutes() > 0 ||
              visibleEnd.getSeconds() > 0 ||
              visibleEnd.getMilliseconds() > 0
                ? 1
                : 0),
          );

          map[key].intervals.push({
            startLabel: timeFormatter.format(visibleStart),
            endLabel: timeFormatter.format(visibleEnd),
            interval,
          });

          if (map[key].intervals.length === 1 && daysSpanned > 1) {
            map[key].colSpan = daysSpanned;
          }
        } else if (!map[key].intervals.length) {
          map[key].isContinuation = true;
        }
      }

      cursor = dayEnd;
    }
  }

  return map;
};

const findInitialWeekStart = (
  plans: GetActuatorPlanDto[] | null | undefined,
): Date => {
  const points = (plans ?? [])
    .flatMap((plan) => plan.schedule ?? [])
    .map((sp) => parseTimestamp(sp.timestamp))
    .filter((date): date is Date => Boolean(date));

  if (points.length === 0) {
    return startOfWeekMonday(new Date());
  }

  points.sort((a, b) => a.getTime() - b.getTime());
  return startOfWeekMonday(points[0]);
};

const DayCell = ({
  value,
  isPlanActive,
  onIntervalClick,
}: {
  value: DayCellValue;
  isPlanActive: boolean;
  onIntervalClick: (interval: IntervalSegment) => void;
}) => {
  if (value.isContinuation) {
    return null;
  }

  if (!value.intervals.length) {
    return <Box sx={{ height: '100%', minHeight: 40 }} />;
  }

  return (
    <Stack
      spacing={0.5}
      sx={{ py: 1, width: '100%', height: '100%', minHeight: 40 }}
    >
      {value.intervals.map((interval, index) => (
        <Chip
          key={`${interval.startLabel}-${interval.endLabel}-${index}`}
          label={`${interval.startLabel} - ${interval.endLabel}`}
          size="small"
          variant="filled"
          onClick={() => onIntervalClick(interval.interval)}
          clickable
          icon={
            isPlanActive ? (
              <CheckCircleRoundedIcon color="success" />
            ) : (
              <ErrorOutlineRoundedIcon color="error" />
            )
          }
          sx={{
            color: (theme) =>
              isPlanActive
                ? theme.palette.mode === 'dark'
                  ? '#C3E9D7'
                  : '#1F5F4D'
                : theme.palette.mode === 'dark'
                  ? '#F7CACA'
                  : '#8F1D1D',
            justifyContent: 'center',
            flex: 1,
            bgcolor: (theme) =>
              alpha(
                isPlanActive
                  ? theme.palette.success.main
                  : theme.palette.error.main,
                0.12,
              ),
            borderColor: (theme) =>
              alpha(
                isPlanActive
                  ? theme.palette.success.main
                  : theme.palette.error.main,
                0.5,
              ),
            borderStyle: isPlanActive ? 'solid' : 'dashed',
            '& .MuiChip-label': {
              px: 1,
              fontSize: '0.8rem',
              //   fontWeight: 600,
            },
          }}
        />
      ))}
    </Stack>
  );
};

const WeekToolbar = ({
  weekStart,
  onPrevious,
  onNext,
  onToday,
}: {
  weekStart: Date;
  onPrevious: () => void;
  onNext: () => void;
  onToday: () => void;
}) => {
  const { t } = useTranslation('week-schedule-manager');

  const weekEnd = addDays(weekStart, 6);
  const weekFormatter = new Intl.DateTimeFormat(DANISH_LOCALE, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: DANISH_TIMEZONE,
  });
  const weekRangeLabel = `${weekFormatter.format(weekStart)} - ${weekFormatter.format(weekEnd)}`;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
        {t('edit.weeklyScheduleTitle')}
      </Typography>
      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ minWidth: 200 }}
        >
          {weekRangeLabel}
        </Typography>
      </Box>

      <Button
        variant="outlined"
        size="small"
        onClick={onToday}
        sx={{ fontSize: '0.7rem', minWidth: 64 }}
      >
        {t('edit.todayButton')}
      </Button>

      <Button
        variant="outlined"
        size="small"
        onClick={onPrevious}
        sx={{ maxWidth: 32, minWidth: 32 }}
      >
        <ChevronLeftIcon fontSize="small" />
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={onNext}
        sx={{ maxWidth: 32, minWidth: 32 }}
      >
        <ChevronRightIcon fontSize="small" />
      </Button>
    </Stack>
  );
};

const ScheduleDatagrid = ({
  plans,
  isLoading = false,
  gatewayNamesById = {},
}: ScheduleDatagridProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('week-schedule-manager');
  const { t: tTime } = useTranslation('time');
  const [postActuatorPlan, { isLoading: isPosting }] =
    usePostActuatorPlanMutation();

  const [weekStart, setWeekStart] = useState<Date>(() =>
    findInitialWeekStart(plans),
  );
  const [selectedRowForDelete, setSelectedRowForDelete] =
    useState<ChannelRow | null>(null);
  const [selectedActivation, setSelectedActivation] = useState<{
    row: ChannelRow;
    interval: IntervalSegment;
    from: string;
    to: string;
  } | null>(null);

  const postReplacementSchedule = async (
    row: ChannelRow,
    schedule: ActuatorSetPointDto[],
    successMessage: string,
  ): Promise<boolean> => {
    if (typeof row.plan.gatewayId !== 'number') {
      dispatch(
        showSnackbar({
          message: t('edit.gatewayRequired', {
            defaultValue: 'Select a gateway',
          }),
          severity: 'warning',
        }),
      );
      return false;
    }

    const result = await postActuatorPlan({
      gatewayId: row.plan.gatewayId,
      postActuatorPlanDto: {
        channel: row.plan.channel ?? null,
        schedule,
      },
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: successMessage,
          severity: 'success',
        }),
      );
      return true;
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
    return false;
  };

  const rows = getPlansForDisplay(plans).map((plan, idx) => {
    const intervals = buildIntervals(plan.schedule);
    const dayMap = buildWeekDayIntervals(intervals, weekStart);
    const channelKey = plan.channel ?? '';
    const emptyCell: DayCellValue = { intervals: [], colSpan: 1 };
    const planGatewayId = plan.gatewayId;

    const gatewayLabel =
      typeof planGatewayId === 'number'
        ? (gatewayNamesById[planGatewayId] ??
          t('edit.gatewayWithId', {
            id: planGatewayId,
            defaultValue: 'Gateway {{id}}',
          }))
        : t('columns.unknown');

    return {
      id: `${plan.gatewayId ?? 'unknown'}-${plan.channel ?? 'unknown'}-${plan.created ?? 'n/a'}-${idx}`,
      gatewayName: gatewayLabel,
      channelLabel: channelKey
        ? t(`edit.channelOptions.${channelKey}`)
        : t('columns.unknown'),
      isActivePlan: Boolean(plan.active),
      plan,
      d0: dayMap[toDateKey(addDays(weekStart, 0))] ?? emptyCell,
      d1: dayMap[toDateKey(addDays(weekStart, 1))] ?? emptyCell,
      d2: dayMap[toDateKey(addDays(weekStart, 2))] ?? emptyCell,
      d3: dayMap[toDateKey(addDays(weekStart, 3))] ?? emptyCell,
      d4: dayMap[toDateKey(addDays(weekStart, 4))] ?? emptyCell,
      d5: dayMap[toDateKey(addDays(weekStart, 5))] ?? emptyCell,
      d6: dayMap[toDateKey(addDays(weekStart, 6))] ?? emptyCell,
    } as ChannelRow;
  });

  const openActivationEditor = (row: ChannelRow, interval: IntervalSegment) => {
    if (
      typeof interval.startSetpointIndex !== 'number' ||
      typeof interval.endSetpointIndex !== 'number'
    ) {
      dispatch(
        showSnackbar({
          message: t('edit.activationEditUnsupported', {
            defaultValue: 'Only concrete activations can be edited.',
          }),
          severity: 'warning',
        }),
      );
      return;
    }

    setSelectedActivation({
      row,
      interval,
      from: toDateTimeLocalInput(interval.start),
      to: toDateTimeLocalInput(interval.end),
    });
  };

  const closeActivationEditor = () => {
    setSelectedActivation(null);
  };

  const saveActivationEdit = async () => {
    if (!selectedActivation) return;

    const startTime = parseDateTimeLocalInput(selectedActivation.from);
    const endTime = parseDateTimeLocalInput(selectedActivation.to);

    if (!startTime || !endTime || endTime <= startTime) {
      dispatch(
        showSnackbar({
          message: t('edit.invalidActivationRange', {
            defaultValue: 'Select a valid start and end date/time.',
          }),
          severity: 'warning',
        }),
      );
      return;
    }

    const sortedSchedule = sortScheduleSetpoints(
      selectedActivation.row.plan.schedule,
    );
    const startIndex = selectedActivation.interval.startSetpointIndex;
    const endIndex = selectedActivation.interval.endSetpointIndex;

    if (
      typeof startIndex !== 'number' ||
      typeof endIndex !== 'number' ||
      !sortedSchedule[startIndex] ||
      !sortedSchedule[endIndex]
    ) {
      dispatch(
        showSnackbar({
          message: t('edit.activationNotFound', {
            defaultValue: 'Could not find the selected activation.',
          }),
          severity: 'error',
        }),
      );
      return;
    }

    sortedSchedule[startIndex] = {
      ...sortedSchedule[startIndex],
      timestamp: startTime.toISOString(),
      value: '0',
    };
    sortedSchedule[endIndex] = {
      ...sortedSchedule[endIndex],
      timestamp: endTime.toISOString(),
      value: '100',
    };

    const updatedSchedule = sortScheduleSetpoints(sortedSchedule);
    const didPost = await postReplacementSchedule(
      selectedActivation.row,
      updatedSchedule,
      t('edit.activationUpdatedSuccess', {
        defaultValue: 'Activation updated successfully.',
      }),
    );

    if (didPost) {
      closeActivationEditor();
    }
  };

  const deleteActivation = async () => {
    if (!selectedActivation) return;

    const sortedSchedule = sortScheduleSetpoints(
      selectedActivation.row.plan.schedule,
    );
    const startIndex = selectedActivation.interval.startSetpointIndex;
    const endIndex = selectedActivation.interval.endSetpointIndex;

    if (
      typeof startIndex !== 'number' ||
      typeof endIndex !== 'number' ||
      !sortedSchedule[startIndex] ||
      !sortedSchedule[endIndex]
    ) {
      dispatch(
        showSnackbar({
          message: t('edit.activationNotFound', {
            defaultValue: 'Could not find the selected activation.',
          }),
          severity: 'error',
        }),
      );
      return;
    }

    const updatedSchedule = sortedSchedule.filter(
      (_, index) => index !== startIndex && index !== endIndex,
    );
    const scheduleToPost =
      updatedSchedule.length > 0
        ? updatedSchedule
        : buildClearScheduleFallback();

    const didPost = await postReplacementSchedule(
      selectedActivation.row,
      scheduleToPost,
      t('edit.activationDeletedSuccess', {
        defaultValue: 'Activation deleted successfully.',
      }),
    );

    if (didPost) {
      closeActivationEditor();
    }
  };

  const requestClearSchedule = (row: ChannelRow) => {
    setSelectedRowForDelete(row);
  };

  const closeClearDialog = () => {
    setSelectedRowForDelete(null);
  };

  const clearEntireSchedule = async () => {
    if (!selectedRowForDelete) return;

    const didPost = await postReplacementSchedule(
      selectedRowForDelete,
      buildClearScheduleFallback(),
      t('edit.clearSuccess', {
        name: selectedRowForDelete.gatewayName,
        defaultValue: 'Schedule cleared successfully.',
      }),
    );

    if (didPost) {
      closeClearDialog();
    }
  };

  const columns: GridColDef<ChannelRow>[] = [
    {
      field: 'gatewayName',
      headerName: t('edit.gatewayLabel', { defaultValue: 'Device' }),
      width: 200,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerClassName: 'gateway-column-header',
      renderCell: (params) => (
        <Stack
          justifyContent="center"
          sx={{ width: '100%', minHeight: 40, py: 0.25 }}
        >
          <Typography variant="body2" color="text.primary" fontWeight={600}>
            {params.row.gatewayName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.channelLabel}
          </Typography>
        </Stack>
      ),
    },
    ...WEEKDAY_KEYS.map((dayKey, index) => {
      const date = addDays(weekStart, index);
      const dateLabel = new Intl.DateTimeFormat(DANISH_LOCALE, {
        day: '2-digit',
        month: 'short',
        timeZone: DANISH_TIMEZONE,
      }).format(date);
      const isTodayColumn = toDateKey(date) === toDateKey(new Date());

      return {
        field: `d${index}`,
        headerName: `${tTime(`weekdays.${dayKey}.indefinite`)} ${dateLabel}`,
        // minWidth: 170,
        flex: 1,
        sortable: false,
        filterable: false,
        resizable: false,
        orderable: false,
        disableColumnMenu: true,
        colSpan: (params) =>
          (params.value as DayCellValue | undefined)?.colSpan ?? 1,
        headerClassName: isTodayColumn ? 'today-column-header' : undefined,
        cellClassName: isTodayColumn ? 'today-column-cell' : undefined,
        renderCell: (
          params: GridRenderCellParams<ChannelRow, DayCellValue>,
        ) => {
          if (!params.value) return null;
          return (
            <DayCell
              value={params.value}
              isPlanActive={Boolean(params.row.isActivePlan)}
              onIntervalClick={(interval) =>
                openActivationEditor(params.row, interval)
              }
            />
          );
        },
      };
    }),
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<ChannelRow, DayCellValue>) => {
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              height: '100%',
              alignItems: 'center',
            }}
          >
            <Tooltip title={t('edit.deletePlanTooltip')} placement="top">
              <IconButton
                size="small"
                onClick={() => requestClearSchedule(params.row)}
                disabled={isPosting}
                sx={{ borderRadius: 2, '&:hover': { color: 'error.main' } }}
              >
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <WeekToolbar
        weekStart={weekStart}
        onPrevious={() => setWeekStart((prev) => addDays(prev, -7))}
        onNext={() => setWeekStart((prev) => addDays(prev, 7))}
        onToday={() => setWeekStart(startOfWeekMonday(new Date()))}
      />

      {isLoading ? (
        <Stack spacing={1} sx={{ p: 1.5 }}>
          <Skeleton variant="rounded" height={52} />
          <Skeleton variant="rounded" height={52} />
          <Skeleton variant="rounded" height={52} />
        </Stack>
      ) : rows.length === 0 ? (
        <Box
          sx={{
            px: 2,
            pb: 2,
            pt: 1.5,
            color: 'text.secondary',
            fontSize: '0.875rem',
          }}
        >
          {t('edit.noScheduleEntries')}
        </Box>
      ) : (
        <DataGridPremium
          autoHeight
          rows={rows}
          columns={columns}
          pinnedColumns={{ left: ['gatewayName'], right: ['actions'] }}
          hideFooter
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 400,
            },
            '& .today-column-header': {
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.success.main, 0.24)
                  : alpha(theme.palette.success.main, 0.12),
            },
            '& .today-column-header .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
            },
            '& .today-column-cell': {
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.success.main, 0.12)
                  : alpha(theme.palette.success.main, 0.06),
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              justifyContent: 'center',
            },
            '& .gateway-column-header .MuiDataGrid-columnHeaderTitleContainer':
              {
                justifyContent: 'flex-start',
              },
          }}
        />
      )}

      <Dialog
        open={Boolean(selectedActivation)}
        onClose={closeActivationEditor}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('edit.editActivationTitle', {
            defaultValue: 'Edit activation',
          })}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              type="datetime-local"
              label={t('edit.activationFrom', {
                defaultValue: 'Activation from',
              })}
              value={selectedActivation?.from ?? ''}
              onChange={(event) =>
                setSelectedActivation((prev) =>
                  prev
                    ? {
                        ...prev,
                        from: event.target.value,
                      }
                    : prev,
                )
              }
              fullWidth
              slotProps={{
                inputLabel: { shrink: true }
              }}
            />
            <TextField
              type="datetime-local"
              label={t('edit.activationTo', {
                defaultValue: 'Activation to',
              })}
              value={selectedActivation?.to ?? ''}
              onChange={(event) =>
                setSelectedActivation((prev) =>
                  prev
                    ? {
                        ...prev,
                        to: event.target.value,
                      }
                    : prev,
                )
              }
              fullWidth
              slotProps={{
                inputLabel: { shrink: true }
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActivationEditor} disabled={isPosting}>
            {t('edit.previewCancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button color="error" onClick={deleteActivation} disabled={isPosting}>
            {t('edit.deleteActivationButton', {
              defaultValue: 'Delete activation',
            })}
          </Button>
          <Button
            variant="contained"
            onClick={saveActivationEdit}
            disabled={isPosting}
          >
            {t('edit.saveActivationButton', {
              defaultValue: 'Save activation',
            })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(selectedRowForDelete)}
        onClose={closeClearDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('edit.clearScheduleDialogTitle', {
            defaultValue: 'Clear schedule',
          })}
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            {t('edit.clearScheduleDialogMessage', {
              defaultValue:
                'Are you sure you want to clear this schedule? A fallback activation will be posted to keep the plan valid.',
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {selectedRowForDelete?.gatewayName} -{' '}
            {selectedRowForDelete?.channelLabel}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeClearDialog} disabled={isPosting}>
            {t('edit.previewCancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={clearEntireSchedule}
            disabled={isPosting}
          >
            {t('edit.clearButton', { defaultValue: 'Clear Schedule' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ScheduleDatagrid;
